import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, BrowserRouter } from 'react-router-dom';
import { routes } from './routes';
import { Layout } from '../components/Layout';
import Login from './pages/login';
import Logout from './pages/logout';
import DataPrivacyRequest from '@/app/pages/data-privacy-request';
import Onboarding from './pages/onboarding';
import OnboardingISO from './pages/onboarding/iso';
import AccountManagement from './pages/account_management';

// Auditor view imports
import { AuditorRoutesWithLayout } from './auditor-routes';
import { useNavigate } from 'react-router-dom';
import { ControlsProvider } from '@/components/ui/ControlCombobox';
import { TenancyContextProvider } from '@/contexts/TenancyContextProvider';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { ResetProvider } from '@/utils/stateReset';
import { PaywallDialogProvider } from '@/utils/paywall-navigation-utils';
import EssentialEight from './pages/essential-eight';
import PrivacyActQuestionnaire from './pages/privacy-act';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { getAvailableFeatures } from '@/utils/featuresUtil';
import { initI18n } from '@/i18n/i18n';
import { startDomTranslator } from '@/i18n/domTranslator';

// Force all console methods to show output
// const originalConsole = { ...console };
// Object.keys(console).forEach((key) => {
//   if (typeof console[key] === 'function') {
//     console[key] = function() {
//       originalConsole[key].apply(console, arguments);
//     };
//   }
// });

const App = () => {
  const availableFeatures = getAvailableFeatures();
  const isSelfServiceEnabled = availableFeatures.some(f => f.name === 'Self-Service');
  const location = useLocation();
  const navigate = useNavigate();

  // Global i18n initialization and DOM translator (runs once)
  useEffect(() => {
    initI18n();
    const ctl = startDomTranslator();
    return () => ctl.disconnect();
  }, []);

  // Re-translate after route changes
  useEffect(() => {
    try {
      (window as any).deiterateI18n?.translatePage?.();
    } catch {}
  }, [location.pathname]);

  // Redirect to index_vanilla.html if at root path
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/index' || location.pathname === '/index.html') {
      // window.location.href = '/index_vanilla.html';
      navigate('/dashboard');
    }
  }, [location, navigate]);

  // Check path conditions after all hooks are called
  const isLoginPage = location.pathname === '/login';
  const isLogoutPage = location.pathname === '/logout';
  const isOnboardingPage = location.pathname.startsWith('/signup');
  const isAuditorPage = location.pathname.startsWith('/auditor-view');
  const isAccountManagementPage = location.pathname.startsWith('/account_management');
  const isEssentialEightPage = location.pathname.startsWith('/essential-eight');
  const isPrivacyActPage = location.pathname.startsWith('/privacy-act');

  // Render different layouts based on path, but without early returns
  let content;

  if (location.pathname === '/data-privacy-request') {
    return (
      <Routes>
        <Route path="/data-privacy-request" element={<DataPrivacyRequest />} />
      </Routes>
    );
  }

  if (isLoginPage) {
    content = (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  } else if (isLogoutPage) {
    content = (
      <Routes>
        <Route path="/logout" element={<Logout />} />
      </Routes>
    );
  } else if (isOnboardingPage && isSelfServiceEnabled) {
    content = (
      <ThemeProvider>
        <Routes>
          <Route path="/signup" element={<Onboarding />} />
          {/* <Route path="/onboarding/iso" element={<OnboardingISO />} /> */}
        </Routes>
      </ThemeProvider>
    );
  } else if (isAuditorPage) {
    console.log('Rendering auditor routes for path:', location.pathname);
    content = (
      <ErrorBoundary>
        <ResetProvider>
          <TenancyContextProvider>
            <ControlsProvider>
              <AuditorRoutesWithLayout />
            </ControlsProvider>
          </TenancyContextProvider>
        </ResetProvider>
      </ErrorBoundary>
    );
  } else if (isAccountManagementPage) {
    content = (
      <Routes>
        <Route path="/account_management/index.html" element={<AccountManagement />} />
      </Routes>
    );
  } else if (isEssentialEightPage) {
    content = (
      <Layout>
        <Routes>
          <Route path="/essential-eight" element={
            <ThemeProvider>
              <PaywallDialogProvider>
                <EssentialEight />
              </PaywallDialogProvider>
            </ThemeProvider>
          } />
        </Routes>
      </Layout>
    );
  } else if (isPrivacyActPage) {
    content = (
      <Layout>
        <Routes>
          <Route path="/privacy-act" element={
            <ThemeProvider>
              <PaywallDialogProvider>
                <PrivacyActQuestionnaire />
              </PaywallDialogProvider>
            </ThemeProvider>
          } />
        </Routes>
      </Layout>
    );
  } else {
    // Render with Layout for all other pages
    content = (
      <Layout>
        <Routes>
          {routes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </Layout>
    );
  }

  return content;
};

export default App;
